import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Space, message } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import StatusTag from "../../components/ui/StatusTag";

const { RangePicker } = DatePicker;

const OvertimeRequests = () => {
  const { request } = useRequest();
  const [requests, setRequests] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({ status: undefined, department: undefined, from: undefined, to: undefined });

  const [actionModal, setActionModal] = useState({ open: false, type: null, record: null });
  const [form] = Form.useForm();

  const fetchRequests = () => {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.department) params.append("department", filters.department);
    if (filters.from) params.append("from", filters.from);
    if (filters.to) params.append("to", filters.to);
    request({
      url: `${apiPath.overtime}?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => setRequests(res?.data?.data || []),
    });
  };

  const fetchDepartments = () => {
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
  };

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const openAction = (type, record) => {
    form.resetFields();
    setActionModal({ open: true, type, record });
  };

  const onSubmitAction = () => {
    form.validateFields().then((values) => {
      const { type, record } = actionModal;
      const urlMap = {
        approve: `${apiPath.overtime}/${record._id}/approve`,
        reject: `${apiPath.overtime}/${record._id}/reject`,
      };
      request({
        url: urlMap[type],
        method: "PUT",
        data: { adminComment: values.comment },
        onSuccess: () => {
          message.success("Overtime request updated");
          setActionModal({ open: false, type: null, record: null });
          fetchRequests();
        },
      });
    });
  };

  const columns = [
    { title: "Employee", dataIndex: ["employee", "name"] },
    { title: "Department", dataIndex: ["employee", "department", "name"] },
    { title: "Date", dataIndex: "date" },
    { title: "Expected Hours", dataIndex: "hours" },
    { title: "Actual Hours", dataIndex: "actualHours", render: (v) => (v != null ? v : "—") },
    { title: "Reason", dataIndex: "reason", ellipsis: true },
    { title: "Status", dataIndex: "status", render: (s) => <StatusTag status={s} /> },
    { title: "Comment", dataIndex: "adminComment" },
    {
      title: "Actions",
      render: (_, record) =>
        record.status === "pending" ? (
          <Space>
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => openAction("approve", record)}>
              Approve
            </Button>
            <Button size="small" danger icon={<CloseOutlined />} onClick={() => openAction("reject", record)}>
              Reject
            </Button>
          </Space>
        ) : null,
    },
  ];

  return (
    <PageTransition>
      <PageHeader title="Overtime Requests" subtitle="Review and approve employee overtime requests" />
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 150 }}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={[
            { value: "pending", label: "Pending" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
          ]}
        />
        <Select
          allowClear
          placeholder="Department"
          style={{ width: 200 }}
          value={filters.department}
          onChange={(v) => setFilters((f) => ({ ...f, department: v }))}
          options={departments.map((d) => ({ value: d._id, label: d.name }))}
        />
        <RangePicker
          onChange={(dates, dateStrings) => setFilters((f) => ({ ...f, from: dateStrings[0] || undefined, to: dateStrings[1] || undefined }))}
        />
      </Space>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={requests}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: <EmptyState message="No overtime requests found" /> }}
      />

      <Modal
        title={actionModal.type === "approve" ? "Approve Overtime Request" : "Reject Overtime Request"}
        open={actionModal.open}
        onCancel={() => setActionModal({ open: false, type: null, record: null })}
        onOk={onSubmitAction}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item name="comment" label="Comment (optional)">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </PageTransition>
  );
};

export default OvertimeRequests;
